import { createSlice } from "@reduxjs/toolkit";
import { userApi, updateProfileApi } from "./userApi";
import { signupApi } from "./signupApi";
import { signinApi } from "./signinApi";
import { profileApi } from "./profileApi";
import { checkUserApi } from "./checkUserApi";
import { sendOtpApi } from "./sendOtpApi";
import { validateOtpApi } from "./validateOtpApi";
import { forgotPasswordSendOtpApi } from "./forgotPasswordSendOtpApi";
import { forgotPasswordResetApi } from "./forgotPasswordResetApi";

const initialState = {
  // User information from API response
  firstName: "",
  lastName: null,
  age: null,
  gender: "",
  role: "",
  email: "",
  phone: null,
  description: null,
  picture: null,
  achievements: [],
  certifications: [],
  topics: [],
  isApproved: false,
  favouriteCoaches: [],
  _id: "",
  fullName: "",
  id: "",
  // Additional state
  avatar: "",
  preferences: [],
  isLoading: false,
  error: null,
  isAuthenticated: false,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      return { ...initialState, isLoading: false };
    },
  },
  extraReducers: (builder) => {
    // Check User API cases
    builder.addCase(checkUserApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(checkUserApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(checkUserApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to check user";
    });

    // Send OTP API cases
    builder.addCase(sendOtpApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(sendOtpApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(sendOtpApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to send OTP";
    });

    // Validate OTP API cases
    builder.addCase(validateOtpApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(validateOtpApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(validateOtpApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to validate OTP";
    });

    // Forgot Password Send OTP API cases
    builder.addCase(forgotPasswordSendOtpApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(forgotPasswordSendOtpApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(forgotPasswordSendOtpApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to send OTP";
    });

    // Forgot Password Reset API cases
    builder.addCase(forgotPasswordResetApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(forgotPasswordResetApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(forgotPasswordResetApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || "Failed to reset password";
    });

    // Existing userApi cases
    builder.addCase(userApi.fulfilled, (state, action) => {
      state.name = action.payload.name;
      state.isLoading = false;
    });
    builder.addCase(userApi.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(userApi.rejected, (state, action) => {
      state.isLoading = false;
      state.name = "No name";
    });

    // Signup API cases
    builder.addCase(signupApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(signupApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.isAuthenticated = true;

      // Update state with user information from API response
      const user = action.payload.data.user;
      state.firstName = user.firstName;
      state.lastName = user.lastName;
      state.age = user.age;
      state.gender = user.gender;
      state.role = user.role;
      state.email = user.email;
      state.phone = user.phone;
      state.description = user.description;
      state.picture = user.picture;
      state.achievements = user.achievements;
      state.certifications = user.certifications;
      state.topics = user.topics;
      state.isApproved = user.isApproved;
      state.favouriteCoaches = user.favouriteCoaches;
      state._id = user._id;
      state.fullName = user.fullName;
      state.id = user.id;
    });
    builder.addCase(signupApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload.message;
      state.isAuthenticated = false;
    });

    // Signin API cases
    builder.addCase(signinApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(signinApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.isAuthenticated = true;

      // Update state with user information from API response
      const user = action.payload.data.user;
      state.firstName = user.firstName;
      state.lastName = user.lastName;
      state.age = user.age;
      state.gender = user.gender;
      state.role = user.role;
      state.email = user.email;
      state.phone = user.phone;
      state.description = user.description;
      state.picture = user.picture;
      state.achievements = user.achievements;
      state.certifications = user.certifications;
      state.topics = user.topics;
      state.isApproved = user.isApproved;
      state.favouriteCoaches = user.favouriteCoaches;
      state._id = user._id;
      state.fullName = user.fullName;
      state.id = user.id;
    });
    builder.addCase(signinApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    });

    // Profile API cases
    builder.addCase(profileApi.pending, (state) => {
      // Don't set loading for profile validation as it's done on component mount
      // and shouldn't block the UI
    });
    builder.addCase(profileApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.isAuthenticated = true;

      // Update state with user information from API response
      const user = action.payload.data.user;
      state.firstName = user.firstName;
      state.lastName = user.lastName;
      state.age = user.age;
      state.gender = user.gender;
      state.role = user.role;
      state.email = user.email;
      state.phone = user.phone;
      state.description = user.description;
      state.picture = user.picture;
      state.achievements = user.achievements;
      state.certifications = user.certifications;
      state.topics = user.topics;
      state.isApproved = user.isApproved;
      state.favouriteCoaches = user.favouriteCoaches;
      state._id = user._id;
      state.fullName = user.fullName;
      state.id = user.id;
    });
    builder.addCase(profileApi.rejected, (state, action) => {
      // Profile validation failed, user is not authenticated
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    });

    // Update Profile API cases
    builder.addCase(updateProfileApi.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateProfileApi.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;

      const user = action.payload.data.user;
      state.firstName = user.firstName;
      state.lastName = user.lastName;
      state.age = user.age;
      state.gender = user.gender;
      state.email = user.email;
      state.phone = user.phone;
      state.picture = user.picture;
      state.fullName = user.fullName;
    });
    builder.addCase(updateProfileApi.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
  },
});

export const { clearError, logout } = userSlice.actions;

export default userSlice.reducer;
